import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LANGUAGE_LABELS,
  VERDICT_LABELS,
  type Difficulty,
  type ProfileResponse,
  type SubmissionSummary,
} from '@flarecode/shared';
import { api } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * Public user profile page.
 *
 * Visible to anyone (anonymous callers included). Submission rows are only
 * clickable when the logged-in viewer owns the profile — on others' profiles
 * the rows are static, matching the "submission source stays private"
 * privacy choice from Tier 2b planning.
 *
 * The single `/api/users/:username/profile` endpoint delivers everything
 * needed to render this page; further per-section fetches are not required.
 */
export function ProfilePage() {
  const { username = '' } = useParams();
  const { user: viewer } = useCurrentUser();

  const { data, isLoading, error } = useQuery<ProfileResponse>({
    queryKey: ['profile', username.toLowerCase()],
    queryFn: () => api.getProfile(username),
    enabled: !!username,
    retry: (count, err) => {
      // Don't retry 404: the user doesn't exist, re-fetching won't help.
      if (err instanceof Error && (err as { status?: number }).status === 404) return false;
      return count < 2;
    },
  });

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="error">User not found.</p>;
  if (!data) return null;

  const isOwnProfile =
    !!viewer && viewer.username.toLowerCase() === data.user.username.toLowerCase();

  return (
    <section className="profile">
      <ProfileHeader user={data.user} />
      <StatsBlock stats={data.stats} />
      <RecentSubmissions items={data.recentSubmissions} isOwnProfile={isOwnProfile} />
    </section>
  );
}

function ProfileHeader({ user }: { user: ProfileResponse['user'] }) {
  const joinedMs = Date.parse(user.createdAt);
  const joinedLabel = Number.isFinite(joinedMs)
    ? new Date(joinedMs).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;
  return (
    <div className="profile-header">
      <h2>@{user.username}</h2>
      {joinedLabel && <p className="profile-joined">Member since {joinedLabel}</p>}
    </div>
  );
}

function StatsBlock({ stats }: { stats: ProfileResponse['stats'] }) {
  const acceptancePercent =
    stats.totalSubmissions > 0
      ? Math.round((stats.acceptedSubmissions / stats.totalSubmissions) * 100)
      : 0;

  return (
    <div className="profile-stats">
      <div className="stat-grid">
        <StatTile label="Solved" value={stats.totalSolved} />
        <StatTile
          label="Acceptance"
          value={stats.totalSubmissions > 0 ? `${acceptancePercent}%` : '—'}
          sub={
            stats.totalSubmissions > 0
              ? `${stats.acceptedSubmissions} of ${stats.totalSubmissions}`
              : 'No submissions yet'
          }
        />
        <StatTile
          label="Current streak"
          value={stats.currentStreakDays}
          sub={stats.currentStreakDays === 1 ? 'day' : 'days'}
        />
        <StatTile
          label="Longest streak"
          value={stats.longestStreakDays}
          sub={stats.longestStreakDays === 1 ? 'day' : 'days'}
        />
        <StatTile
          label="Languages"
          value={stats.languagesUsed.length}
          sub={
            stats.languagesUsed.length === 0
              ? '—'
              : stats.languagesUsed.map((l) => LANGUAGE_LABELS[l]).join(', ')
          }
        />
      </div>

      <DifficultyBreakdown
        byDifficulty={stats.solvedByDifficulty}
        totalSolved={stats.totalSolved}
      />
    </div>
  );
}

function StatTile({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="stat-tile">
      <div className="stat-tile-value">{value}</div>
      <div className="stat-tile-label">{label}</div>
      {sub && <div className="stat-tile-sub">{sub}</div>}
    </div>
  );
}

function DifficultyBreakdown({
  byDifficulty,
  totalSolved,
}: {
  byDifficulty: Record<Difficulty, number>;
  totalSolved: number;
}) {
  // Bar width scales against the max count across difficulties, so the
  // visualization stays useful even for users with uneven distributions.
  const max = Math.max(byDifficulty.easy, byDifficulty.medium, byDifficulty.hard, 1);
  return (
    <div className="difficulty-breakdown">
      <h3>Solved by difficulty</h3>
      {totalSolved === 0 ? (
        <p className="empty">No problems solved yet.</p>
      ) : (
        <div className="diff-bars">
          {(['easy', 'medium', 'hard'] as const).map((d) => (
            <div key={d} className={`diff-row difficulty--${d}`}>
              <span className="diff-label">{d}</span>
              <span className="diff-count">{byDifficulty[d]}</span>
              <div className="diff-bar-track">
                <div
                  className="diff-bar-fill"
                  style={{ width: `${(byDifficulty[d] / max) * 100}%` }}
                  aria-hidden
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentSubmissions({
  items,
  isOwnProfile,
}: {
  items: SubmissionSummary[];
  isOwnProfile: boolean;
}) {
  if (items.length === 0) {
    return (
      <div className="recent-submissions">
        <h3>Recent submissions</h3>
        <p className="empty">No submissions yet.</p>
      </div>
    );
  }
  return (
    <div className="recent-submissions">
      <h3>Recent submissions</h3>
      <table className="problem-list">
        <thead>
          <tr>
            <th>When</th>
            <th>Problem</th>
            <th>Language</th>
            <th>Verdict</th>
            <th>Runtime</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const whenLabel = new Date(s.createdAt).toLocaleString();
            const whenCell = isOwnProfile ? (
              <Link to={`/submissions/${s.id}`}>{whenLabel}</Link>
            ) : (
              // Not clickable for other users: submission source is private.
              <span>{whenLabel}</span>
            );
            return (
              <tr key={s.id}>
                <td>{whenCell}</td>
                <td>
                  <Link to={`/problems/${s.problemSlug}`}>{s.problemTitle}</Link>
                </td>
                <td>{LANGUAGE_LABELS[s.language]}</td>
                <td>
                  <span className={`verdict-badge verdict--${s.verdict}`}>
                    {VERDICT_LABELS[s.verdict]}
                  </span>
                </td>
                <td>{s.runtimeMs != null ? `${s.runtimeMs} ms` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
